import * as React from "react"
import { Container, Row, Col, Form } from "react-bootstrap"
import { toast } from "react-toastify"
import TestingPeriodBreakdownGrid from "./TestingPeriodBreakdownGrid"
import PoolGeneralInfo, { PoolVariant } from "./PoolGeneralInfo"
import ConfirmationModal from "../../ConfirmationModal"
import { CloseProgramYearIntervalObservable } from "../../../api/Randoms/CloseProgramIntervalGet"

import { Subscription } from "rxjs"
import PoolData from "../../../api/Randoms/RandomsModel/PoolData"
import PoolGeneralInfoGetObservable from "../../../api/Randoms/PoolGeneralInfoGet"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns"

interface CloseProgramIntervalProps {
  poolId: number
  poolProgramId: number
  poolName: string
  poolData?: PoolData
  onPoolUpdate: () => void
}

interface CloseProgramIntervalState {
  showConfirmationModal: boolean
  showEditPool: boolean,
  poolLoading: boolean
  poolSaving: boolean
  currentIntervalStartDate: string,
  currentIntervalEndDate: string,
  programIntervalStartDate: string,
  programIntervalEndDate: string,
  selectionInterval: string

}
interface ValidationStatus {
  isValid: boolean,
  errorMessage: string
}
export default class CloseProgramInterval extends React.PureComponent<CloseProgramIntervalProps, CloseProgramIntervalState> {
  state: CloseProgramIntervalState = {
    showConfirmationModal: true,
    showEditPool: false,
    poolLoading: false,
    poolSaving: false,
    currentIntervalStartDate: "",
    currentIntervalEndDate: "",
    programIntervalStartDate: "",
    programIntervalEndDate: "",
    selectionInterval: ""

  }
  loadPoolDataSubscription?: Subscription

  closeSubscription?: Subscription
  componentDidMount() {
    this.loadPoolData()
  }

  componentWillUnmount() {
    this.closeSubscription && this.closeSubscription.unsubscribe()
    this.loadPoolDataSubscription && this.loadPoolDataSubscription.unsubscribe()
  }
  loadPoolData = () => {
    this.setState({ poolLoading: true })
    this.loadPoolDataSubscription && this.loadPoolDataSubscription.unsubscribe()
    this.loadPoolDataSubscription = PoolGeneralInfoGetObservable(this.props.poolId).subscribe(
      (data: PoolData) => {
        const currentStartDate = parseISO(data.ProgramIntervalStartDate)
        const currentEndDate = parseISO(data.ProgramIntervalEndDate)
        
        const nextStartDate = new Date(currentStartDate)
        nextStartDate.setFullYear(nextStartDate?.getFullYear() + 1)

        const nextEndDate = new Date (currentEndDate)
        nextEndDate.setFullYear(nextEndDate?.getFullYear() + 1)

        this.setState({
          currentIntervalStartDate: !!currentStartDate.getDate() ? currentStartDate.toISOString() : "",
          currentIntervalEndDate: !!currentEndDate.getDate() ? currentEndDate.toISOString() : "",
          programIntervalStartDate: !!nextStartDate.getDate() ? nextStartDate.toISOString() : "",
          programIntervalEndDate: !!nextEndDate.getDate() ? nextEndDate.toISOString() : "",
          selectionInterval: data.SelectionInterval,
          poolLoading: false
        })
      },
      () => this.setState({ poolLoading: false })
    )
  }
  handleProgramIntervalStartDateChange = (date: Date | null) => {
    this.setState({ programIntervalStartDate: date ? date.toISOString() : "" })
  }
  handleProgramIntervalEndDateChange = (date: Date | null) => {
    this.setState({ programIntervalEndDate: date ? date.toISOString() : "" })
  }

  showConfirmation = () => this.setState({ showConfirmationModal: true })

  hideConfirmation = () => this.setState({ showConfirmationModal: false })

  closeProgram = () => {
    if (!this.isIntervalValid()) {
      return
    }
    this.setState({ poolSaving: true })
    this.closeSubscription && this.closeSubscription.unsubscribe
    this.closeSubscription = CloseProgramYearIntervalObservable({
      PoolID: this.props.poolId,
      SelectionInterval: this.state.selectionInterval,
      DateFrom: format(parseISO(this.state.programIntervalStartDate || ""), "MM/dd/yyyy"),
      DateTo: format(parseISO(this.state.programIntervalEndDate || ""), "MM/dd/yyyy")
    }).subscribe(
      result => {
        toast.success(result)
        this.setState({ showConfirmationModal: false, showEditPool: true, poolSaving: false })
      },
      error => {
        toast.error(error.errorData?.message || error.message)
        this.setState({ poolSaving: false })
      }
    )
  }
  validateInterval = (): ValidationStatus => {
    const result: ValidationStatus = {
      isValid: true,
      errorMessage: ""
    }
    const programIntervalStartDate = this.state.programIntervalStartDate || ""
    const programIntervalEndDate = this.state.programIntervalEndDate || ""
    const startDate = programIntervalStartDate ? parseISO(programIntervalStartDate) : undefined
    const endDate = programIntervalEndDate ? parseISO(programIntervalEndDate) : undefined
    if (!startDate) {
      result.isValid = false
      result.errorMessage = "Date From can't be blank"
      return result
    }
    if (!endDate) {
      result.isValid = false
      result.errorMessage = "Date To can't be blank"
      return result
    }
    if (startDate >= endDate) {
      result.isValid = false
      result.errorMessage = "Date To should be later than Date From"
      return result
    }
    const startYear = startDate.getFullYear()
    const endYear = endDate.getFullYear()
    if (startYear != endYear) {
      result.isValid = false
      result.errorMessage = "The From and To dates cannot be more than one calendar year in duration"
      return result
    }
    const d = new Date();
    const currYear = d.getFullYear()
    if (currYear > startYear) {
      result.isValid = false
      result.errorMessage = "The program year cannot be lesser than current year"
      return result
    }
    const nextYear = currYear + 1
    if (startYear > nextYear) {
      result.isValid = false
      result.errorMessage = "The program year cannot be greater than one consecutive year"
      return result
    }
    return result

  }
  isIntervalValid = () => {
    const validationStatus = this.validateInterval()
    return validationStatus.isValid
  }

  render() {
    const { currentIntervalStartDate, currentIntervalEndDate,
            programIntervalStartDate, programIntervalEndDate,
            selectionInterval } = this.state
    
    const currentStartDate = currentIntervalStartDate ? parseISO(currentIntervalStartDate) : undefined
    const currentEndDate = currentIntervalEndDate ? parseISO(currentIntervalEndDate) : undefined
    const nextStartDate = programIntervalStartDate ? parseISO(programIntervalStartDate) : undefined
    const nextEndDate = programIntervalEndDate ? parseISO(programIntervalEndDate) : undefined

    const validationStatus = this.validateInterval()

    return <>
      <Container fluid className="grid-container" hidden={this.state.showEditPool}>
        <Row>
          <Col>
            <TestingPeriodBreakdownGrid poolId={this.props.poolId} poolProgramId={this.props.poolProgramId} poolName={this.props.poolName} unmaskDonorId={0} />
          </Col>
        </Row>
      </Container>
      {this.state.showEditPool && <PoolGeneralInfo poolId={this.props.poolId} variant={PoolVariant.CloseProgramYear} poolData={this.props.poolData} onPoolUpdate={this.props.onPoolUpdate} edit />}
      <ConfirmationModal show={this.state.showConfirmationModal}
        title="CONFIRMATION"
        confirmButtonText="VERIFY"
        onConfirm={this.closeProgram}
        onHide={this.hideConfirmation}
        disableConfirmButton={(this.state.poolLoading || this.state.poolSaving || !validationStatus.isValid)}
        spinConfirmButton={this.state.poolSaving}
        disableConfirmOnSpin={true}
        size="lg">
        <>
          <p>
            You are about to close the current program interval <b>{currentStartDate ? format(currentStartDate, "MM/dd/yyyy") : ""} - {currentEndDate ? format(currentEndDate, "MM/dd/yyyy") : ""}</b> for pool <b className="text-capitalize">{this.props.poolName}</b>.
          </p>
          <p>
            Once the current program interval is closed no further random selections can be run for that interval.
          </p>
          <p>
            A new interval will be created and incremented by one year. All other pool settings and criteria will remain the same as the current interval. Program compliance calculations will  be reset to reflect the new program interval.
          </p>
          <p>
            <Form.Row >
              <Form.Label column xs={2} md={1} className="text-left"></Form.Label>
              <Form.Label column xs={6} md={4} className="text-left">Program Interval (New):</Form.Label>
              <Col xs={6} lg={2} className="mb-1 mb-lg-0">
                <DatePicker className="form-control"
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  required
                  popperPlacement="top-end"
                  selected={nextStartDate}
                  enableTabLoop={false}
                  onChange={date => this.handleProgramIntervalStartDateChange(date as Date)} />
              </Col>
              <Form.Label column xs={6} md={4} lg={2} xl={1} className="text-right pr-4">To</Form.Label>
              <Col xs={6} lg={2}>
                <DatePicker className="form-control"
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  required
                  popperPlacement="top-end"
                  selected={nextEndDate}
                  enableTabLoop={false}
                  onChange={date => this.handleProgramIntervalEndDateChange(date as Date)} />
              </Col>
            </Form.Row>
            {!validationStatus.isValid && 
            <Form.Row >
              <Form.Label column xs={2} md={1} className="text-left"></Form.Label>
              <Form.Label column xs={6} md={4} className="text-left"></Form.Label>
              <Col xs={18} lg={6}><Form.Control.Feedback type="invalid" className="d-block">{validationStatus.errorMessage}</Form.Control.Feedback></Col>
            </Form.Row>}
            <Form.Row >
              <Form.Label column xs={2} md={1} className="text-left"></Form.Label>
              <Form.Label column xs={6} md={4} className="text-left">Selection Interval:</Form.Label>
              <Form.Label column xs={6} md={4} lg={2} xl={1} className="text-left">{selectionInterval}</Form.Label>
            </Form.Row>
          </p>
          <p>
            Any pool configuration changes to the new program interval pool must be made on the pool detail page before the first selection is run. Once the first selection has been performed, most pool configurations will be locked.
          </p>
          <p>To continue press Verify.</p>
        </>
      </ConfirmationModal>
    </>
  }
}
